import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Search, Wallet, Pencil, FileText, ArrowLeftRight, FileSpreadsheet, FileDown, Upload, Trash2, PackageOpen, ClipboardEdit, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { exportToExcel, exportToPDF } from "@/lib/exports";
import * as XLSX from "xlsx";

const YEARS = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i);
const STATUSES = ["In Stock", "Delivered"];
const BASE_EXPORT_COLS = [
  { key: "model", label: "Model" },
  { key: "model_code", label: "Model Code" },
  { key: "chassis_number", label: "Chassis" },
  { key: "engine_number", label: "Engine" },
  { key: "color", label: "Color" },
  { key: "manufacturing_year", label: "Year" },
  { key: "branch", label: "Branch" },
  { key: "status", label: "Status" },
];
const NDP_COL = { key: "ndp", label: "NDP (₹)" };

export default function VehicleStock() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [branches, setBranches] = useState([]);
  const [q, setQ] = useState("");
  const [fBranch, setFBranch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fModel, setFModel] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const updateFileRef = useRef(null);
  const [updateSummary, setUpdateSummary] = useState(null); // {updated, failed, total, errors[]}

  const fetchAll = async () => {
    const params = {};
    if (q) params.q = q;
    if (fBranch) params.branch = fBranch;
    if (fStatus) params.status = fStatus;
    if (fModel) params.model = fModel;
    const { data } = await api.get("/vehicles", { params });
    setVehicles(data);
  };

  useEffect(() => { api.get("/branches").then(({ data }) => setBranches(data.branches)); }, []);
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [q, fBranch, fStatus, fModel]);

  const models = useMemo(() => [...new Set(vehicles.map(v => v.model))], [vehicles]);
  const canSeeNdp = user?.role === "admin" || user?.role === "finance";
  const canEditNdp = user?.role === "admin";
  const EXPORT_COLS = canSeeNdp ? [...BASE_EXPORT_COLS, NDP_COL] : BASE_EXPORT_COLS;
  const fmtInr = (n) => new Intl.NumberFormat("en-IN").format(Math.round(Number(n) || 0));

  const changeStatus = async (v, newStatus) => {
    try {
      await api.patch(`/vehicles/${v.id}`, { status: newStatus });
      toast.success(`Vehicle marked ${newStatus}`);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const data = await file.arrayBuffer();
      const wb = XLSX.read(data, { cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const parseYear = (v) => {
        if (v == null || v === "") return new Date().getFullYear();
        if (v instanceof Date) return v.getFullYear();
        const s = String(v).trim();
        // Extract 4-digit year from anything like "2026", "2026.0", "01/04/2026", "2026-01-01"
        const m = s.match(/(19|20)\d{2}/);
        if (m) return Number(m[0]);
        const n = Number(s);
        if (!isNaN(n) && n > 1900 && n < 2100) return Math.floor(n);
        return new Date().getFullYear();
      };
      const mapped = rows.map(r => ({
        model: String(r.model ?? r.Model ?? "").trim(),
        model_code: String(r.model_code ?? r["Model Code"] ?? r.ModelCode ?? r.code ?? "").trim(),
        chassis_number: String(r.chassis_number ?? r["Chassis Number"] ?? r.chassis ?? r.Chassis ?? "").trim(),
        engine_number: String(r.engine_number ?? r["Engine Number"] ?? r.engine ?? r.Engine ?? "").trim(),
        color: String(r.color ?? r.Color ?? "").trim(),
        manufacturing_year: parseYear(r.manufacturing_year ?? r.Year ?? r.year ?? r["Manufacturing Year"]),
        branch: String(r.branch ?? r.Branch ?? "").trim(),
      })).filter(v => v.model && v.chassis_number && v.engine_number && v.branch);
      if (mapped.length === 0) { toast.error("No valid rows found. Required columns: model, chassis_number, engine_number, color, manufacturing_year, branch (model_code optional)"); return; }
      const { data: res } = await api.post("/vehicles/bulk", { vehicles: mapped });
      toast.success(`Imported ${res.inserted} vehicles${res.skipped ? `, skipped ${res.skipped}` : ""}`);
      if (res.errors?.length) console.warn("Bulk import errors:", res.errors);
      fetchAll();
    } catch (err) { toast.error("Failed to parse Excel file: " + err.message); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  const downloadTemplate = () => {
    const template = [{
      model: "Atul Elite Plus", model_code: "ELP-2026",
      chassis_number: "CH2026XXXX", engine_number: "EN2026XXXX",
      color: "White", manufacturing_year: 2026, branch: "Purnea HO"
    }];
    const ws = XLSX.utils.json_to_sheet(template);
    // Force the manufacturing_year column to text format so Excel doesn't convert to date
    if (ws["!cols"]) ws["!cols"] = [];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Vehicles");
    XLSX.writeFile(wb, "vehicle_import_template.xlsx");
  };

  const downloadUpdateTemplate = () => {
    // Pre-populate with current vehicles so admin can edit in place.
    const rows = vehicles.map(v => ({
      "Model": v.model || "",
      "Model Code": v.model_code || "",
      "Chassis No.": v.chassis_number || "",
      "Engine No.": v.engine_number || "",
      "Color": v.color || "",
      "Year": v.manufacturing_year || "",
      "Branch": v.branch || "",
      "NDP (₹)": canSeeNdp ? (v.ndp || 0) : "",
      "Status": v.status || "",
    }));
    if (rows.length === 0) {
      // Empty template with a sample row for reference
      rows.push({
        "Model": "Atul Elite Plus", "Model Code": "ELP-2026",
        "Chassis No.": "CH2026XXXX", "Engine No.": "EN2026XXXX",
        "Color": "White", "Year": 2026, "Branch": "Purnea HO",
        "NDP (₹)": 250000, "Status": "In Stock",
      });
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Bulk Update");
    XLSX.writeFile(wb, "vehicle_bulk_update_template.xlsx");
    toast.success(`Update template downloaded with ${rows.length} row(s)`);
  };

  const handleUpdateUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const parseYear = (v) => {
        if (v == null || v === "") return null;
        if (v instanceof Date) return v.getFullYear();
        const m = String(v).match(/(19|20)\d{2}/);
        return m ? Number(m[0]) : null;
      };
      const parseNdp = (v) => {
        if (v == null || v === "") return null;
        const s = String(v).replace(/[₹,\s]/g, "");
        const n = Number(s);
        return isNaN(n) ? null : n;
      };
      const cleaned = [];
      for (const r of raw) {
        const chassis = String(r["Chassis No."] ?? r.chassis_number ?? r.chassis ?? "").trim();
        if (!chassis) continue;
        const row = { chassis_number: chassis };
        const m = String(r["Model"] ?? r.model ?? "").trim(); if (m) row.model = m;
        const mc = String(r["Model Code"] ?? r.model_code ?? "").trim(); if (mc) row.model_code = mc;
        const en = String(r["Engine No."] ?? r.engine_number ?? "").trim(); if (en) row.engine_number = en;
        const c  = String(r["Color"] ?? r.color ?? "").trim(); if (c)  row.color = c;
        const y  = parseYear(r["Year"] ?? r.manufacturing_year); if (y) row.manufacturing_year = y;
        const b  = String(r["Branch"] ?? r.branch ?? "").trim(); if (b) row.branch = b;
        const ndp = parseNdp(r["NDP (₹)"] ?? r.ndp); if (ndp !== null) row.ndp = ndp;
        const s = String(r["Status"] ?? r.status ?? "").trim(); if (s) row.status = s;
        cleaned.push(row);
      }
      if (cleaned.length === 0) {
        toast.error("No rows with a Chassis No. found in the file.");
        return;
      }
      const { data: res } = await api.post("/vehicles/bulk-update", { rows: cleaned });
      setUpdateSummary(res);
      if (res.updated > 0 && res.failed === 0) {
        toast.success(`${res.updated} vehicle(s) updated`);
      } else if (res.updated > 0) {
        toast.success(`${res.updated} updated · ${res.failed} failed — see summary`);
      } else {
        toast.error(`No updates applied · ${res.failed} failed — see summary`);
      }
      fetchAll();
    } catch (err) {
      toast.error("Failed to parse update file: " + err.message);
    } finally {
      if (updateFileRef.current) updateFileRef.current.value = "";
    }
  };

  const deleteAll = async () => {
    const confirm1 = window.prompt(`This will PERMANENTLY delete ALL ${vehicles.length} vehicles, finance records, invoices and transfer history. Type DELETE_ALL to confirm.`);
    if (confirm1 !== "DELETE_ALL") { toast.info("Cancelled"); return; }
    try {
      const { data } = await api.delete("/vehicles", { params: { confirm: "DELETE_ALL" } });
      toast.success(`Deleted ${data.deleted} vehicles and all related records`);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const deleteOne = async (v) => {
    if (!window.confirm(`Delete ${v.model} — ${v.chassis_number}?\n\nThis will also remove its finance & invoice records. This action cannot be undone.`)) return;
    try {
      await api.delete(`/vehicles/${v.id}`);
      toast.success(`${v.model} (${v.chassis_number}) deleted`);
      fetchAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900" data-testid="vehicles-heading">Vehicle Stock</h1>
          <p className="text-sm text-slate-500 mt-1">Manage inventory across all branches</p>
        </div>
        {user?.role === "admin" && (
          <div className="flex gap-2 flex-wrap">
            <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={handleUpload}
              className="hidden" data-testid="excel-upload-input" />
            <input ref={updateFileRef} type="file" accept=".xlsx,.xls" onChange={handleUpdateUpload}
              className="hidden" data-testid="update-upload-input" />
            <button data-testid="download-template" onClick={downloadTemplate}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm text-slate-600">
              Template
            </button>
            <button data-testid="upload-excel-button" onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm">
              <Upload className="w-4 h-4" /> Import Excel
            </button>
            <button data-testid="download-update-template" onClick={downloadUpdateTemplate}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-blue-300 bg-blue-50 hover:bg-blue-100 text-blue-700 text-sm">
              <ClipboardEdit className="w-4 h-4" /> Update Template
            </button>
            <button data-testid="upload-update-button" onClick={() => updateFileRef.current?.click()}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-blue-700 hover:bg-blue-800 text-white text-sm">
              <UploadCloud className="w-4 h-4" /> Upload Update
            </button>
            <button data-testid="export-vehicles-excel"
              onClick={() => exportToExcel("vehicle_stock", vehicles, EXPORT_COLS)}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm">
              <FileSpreadsheet className="w-4 h-4" /> Excel
            </button>
            <button data-testid="export-vehicles-pdf"
              onClick={() => exportToPDF("Vehicle Stock", vehicles, EXPORT_COLS)}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-300 hover:bg-slate-50 text-sm">
              <FileDown className="w-4 h-4" /> PDF
            </button>
            <button data-testid="delete-all-button" onClick={deleteAll}
              className="inline-flex items-center gap-1 px-3 py-2 rounded-md bg-red-600 hover:bg-red-700 text-white text-sm">
              <Trash2 className="w-4 h-4" /> Delete All
            </button>
            <button
              data-testid="add-vehicle-button"
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md bg-brand-orange bg-brand-orange-hover text-white font-medium text-sm transition-colors"
            >
              <Plus className="w-4 h-4" /> Add Vehicle
            </button>
          </div>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="vehicles-search-input"
            placeholder="Search chassis / engine / model"
            value={q} onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-800"
          />
        </div>
        <select data-testid="filter-branch" value={fBranch} onChange={(e) => setFBranch(e.target.value)}
          className="px-3 py-2 rounded-md border border-slate-300 text-sm outline-none bg-white">
          <option value="">All Branches</option>
          {branches.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select data-testid="filter-status" value={fStatus} onChange={(e) => setFStatus(e.target.value)}
          className="px-3 py-2 rounded-md border border-slate-300 text-sm outline-none bg-white">
          <option value="">All Statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="filter-model" value={fModel} onChange={(e) => setFModel(e.target.value)}
          className="px-3 py-2 rounded-md border border-slate-300 text-sm outline-none bg-white">
          <option value="">All Models</option>
          {models.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg table-scroll">
        <table className="data-table w-full" data-testid="vehicles-table">
          <thead>
            <tr>
              <th className="col-sr">SR. No.</th>
              <th>Model</th><th>Model Code</th><th>Chassis No.</th><th>Engine No.</th>
              <th>Color</th><th>Year</th><th>Branch</th>{canSeeNdp && <th className="whitespace-nowrap">NDP (₹)</th>}<th>Status</th><th className="text-right pr-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            {vehicles.length === 0 && (
              <tr><td colSpan={canSeeNdp ? 11 : 10} className="text-center py-10 text-slate-400 text-sm">No vehicles match your filters</td></tr>
            )}
            {vehicles.map((v, i) => (
              <tr key={v.id} data-testid={`vehicle-row-${v.id}`}>
                <td className="col-sr" data-testid={`vehicle-sr-${v.id}`}>{i + 1}</td>
                <td className="font-medium text-slate-900">{v.model}</td>
                <td className="font-mono text-xs">{v.model_code || "—"}</td>
                <td className="font-mono text-xs">{v.chassis_number}</td>
                <td className="font-mono text-xs">{v.engine_number}</td>
                <td>{v.color}</td>
                <td>{v.manufacturing_year}</td>
                <td>{v.branch}</td>
                {canSeeNdp && (
                  <td className="font-mono text-xs text-right pr-4 text-slate-700" data-testid={`vehicle-ndp-${v.id}`}>
                    {v.ndp && Number(v.ndp) > 0 ? `₹ ${fmtInr(v.ndp)}` : <span className="text-slate-300">—</span>}
                  </td>
                )}
                <td>
                  <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${
                    v.status === "Delivered" ? "bg-orange-100 text-orange-800"
                      : v.status === "Transferred" ? "bg-slate-200 text-slate-700"
                      : "bg-emerald-100 text-emerald-800"
                  }`} data-testid={`vehicle-status-${v.id}`}>{v.status}</span>
                </td>
                <td className="text-right pr-6">
                  <div className="flex items-center justify-end gap-2">
                    {v.status === "In Stock" && (user?.role === "admin" || user?.role === "branch") && (
                      <button data-testid={`deliver-vehicle-${v.id}`}
                        onClick={() => changeStatus(v, "Delivered")}
                        className="px-2.5 py-1 rounded-md bg-blue-900 hover:bg-blue-800 text-white text-xs">
                        Deliver
                      </button>
                    )}
                    {v.status === "Delivered" && user?.role === "admin" && (
                      <button data-testid={`return-stock-${v.id}`}
                        onClick={() => changeStatus(v, "In Stock")}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-amber-500 hover:bg-amber-600 text-white text-xs">
                        <PackageOpen className="w-3 h-3" /> Return to Stock
                      </button>
                    )}
                    {(user?.role === "admin" || user?.role === "pdi") && (
                      <button data-testid={`transfer-vehicle-${v.id}`}
                        onClick={() => navigate("/transfers", { state: { vehicleId: v.id } })}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-200 hover:bg-slate-300 text-slate-800 text-xs">
                        <ArrowLeftRight className="w-3 h-3" /> Transfer
                      </button>
                    )}
                    {v.status === "Delivered" && (user?.role === "admin" || user?.role === "finance") && (
                      <button data-testid={`finance-vehicle-${v.id}`}
                        onClick={() => navigate(`/finance/${v.id}`)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-orange-500 hover:bg-orange-600 text-white text-xs">
                        <Wallet className="w-3 h-3" /> Finance
                      </button>
                    )}
                    {v.status === "Delivered" && (
                      <button data-testid={`invoice-vehicle-${v.id}`}
                        onClick={() => navigate(`/invoice/${v.id}`)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-white text-xs">
                        <FileText className="w-3 h-3" /> Invoice
                      </button>
                    )}
                    {user?.role === "admin" && (
                      <>
                        <button data-testid={`edit-vehicle-${v.id}`}
                          onClick={() => setEditing(v)}
                          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" aria-label="Edit">
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button data-testid={`delete-vehicle-${v.id}`}
                          onClick={() => deleteOne(v)}
                          className="p-1.5 rounded-md hover:bg-red-50 text-red-500" aria-label="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(showAdd || editing) && (
        <VehicleForm
          initial={editing}
          branches={branches}
          years={YEARS}
          canEditNdp={canEditNdp}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSaved={() => { setShowAdd(false); setEditing(null); fetchAll(); }}
        />
      )}

      {updateSummary && (
        <BulkUpdateSummary summary={updateSummary} onClose={() => setUpdateSummary(null)} />
      )}
    </div>
  );
}

function BulkUpdateSummary({ summary, onClose }) {
  const { updated = 0, failed = 0, total = 0, errors = [] } = summary || {};
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="bulk-update-summary">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Bulk Update Summary</div>
            <h2 className="font-heading text-xl font-semibold text-slate-900">
              {updated} updated · {failed} failed
              <span className="text-slate-400 text-sm font-normal ml-2">of {total} rows</span>
            </h2>
          </div>
          <button data-testid="bulk-update-close" onClick={onClose}
            className="text-slate-400 hover:text-slate-600 text-2xl leading-none">×</button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200">
              <div className="text-xs text-emerald-700 uppercase font-semibold">Updated</div>
              <div className="text-2xl font-bold text-emerald-800" data-testid="bulk-update-count">{updated}</div>
            </div>
            <div className="p-3 rounded-md bg-red-50 border border-red-200">
              <div className="text-xs text-red-700 uppercase font-semibold">Failed</div>
              <div className="text-2xl font-bold text-red-800" data-testid="bulk-failed-count">{failed}</div>
            </div>
            <div className="p-3 rounded-md bg-slate-50 border border-slate-200">
              <div className="text-xs text-slate-600 uppercase font-semibold">Total Rows</div>
              <div className="text-2xl font-bold text-slate-800">{total}</div>
            </div>
          </div>
          {errors.length === 0 ? (
            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md p-3">
              All rows updated successfully.
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Errors</div>
              <div className="border border-red-200 rounded-md overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-red-50 text-xs uppercase tracking-wider text-red-700">
                    <tr>
                      <th className="px-3 py-2 text-left w-12">Row</th>
                      <th className="px-3 py-2 text-left">Chassis No.</th>
                      <th className="px-3 py-2 text-left">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {errors.map((e, i) => (
                      <tr key={i} className="hover:bg-red-50/50">
                        <td className="px-3 py-2 text-slate-500">{e.row}</td>
                        <td className="px-3 py-2 font-mono text-xs">{e.chassis_number || "—"}</td>
                        <td className="px-3 py-2 text-red-700">{e.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex justify-end">
          <button data-testid="bulk-update-summary-ok" onClick={onClose}
            className="px-4 py-2 rounded-md bg-brand-blue bg-brand-blue-hover text-white text-sm">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function VehicleForm({ initial, branches, years, canEditNdp, onClose, onSaved }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(initial || {
    model: "", chassis_number: "", engine_number: "", color: "",
    manufacturing_year: years[0], branch: branches[0] || "Purnea HO", ndp: "",
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const ndpNum = form.ndp === "" || form.ndp == null ? 0 : Number(form.ndp);
      if (isEdit) {
        const payload = {
          model: form.model, model_code: form.model_code || "",
          chassis_number: form.chassis_number,
          engine_number: form.engine_number, color: form.color,
          manufacturing_year: Number(form.manufacturing_year), branch: form.branch,
        };
        if (canEditNdp) payload.ndp = ndpNum;
        await api.patch(`/vehicles/${initial.id}`, payload);
        toast.success("Vehicle updated");
      } else {
        await api.post("/vehicles", {
          ...form,
          model_code: form.model_code || "",
          manufacturing_year: Number(form.manufacturing_year),
          ndp: canEditNdp ? ndpNum : 0,
        });
        toast.success("Vehicle added to stock");
      }
      onSaved();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" data-testid="vehicle-form-modal">
      <form onSubmit={save} className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6">
        <div className="font-heading text-xl font-semibold mb-4">{isEdit ? "Edit Vehicle" : "Add New Vehicle"}</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Model" testid="vf-model">
            <input required value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })}
              className="input" data-testid="vf-model-input" />
          </Field>
          <Field label="Model Code" testid="vf-model-code">
            <input value={form.model_code || ""} onChange={(e) => setForm({ ...form, model_code: e.target.value })}
              placeholder="e.g. ELP-2026" className="input" data-testid="vf-model-code-input" />
          </Field>
          <Field label="Color" testid="vf-color">
            <input required value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })}
              className="input" data-testid="vf-color-input" />
          </Field>
          <Field label="Chassis Number" testid="vf-chassis">
            <input required value={form.chassis_number} onChange={(e) => setForm({ ...form, chassis_number: e.target.value })}
              className="input font-mono" data-testid="vf-chassis-input" />
          </Field>
          <Field label="Engine Number" testid="vf-engine">
            <input required value={form.engine_number} onChange={(e) => setForm({ ...form, engine_number: e.target.value })}
              className="input font-mono" data-testid="vf-engine-input" />
          </Field>
          <Field label="Manufacturing Year" testid="vf-year">
            <select value={form.manufacturing_year} onChange={(e) => setForm({ ...form, manufacturing_year: e.target.value })}
              className="input" data-testid="vf-year-input">
              {years.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
          <Field label="Branch" testid="vf-branch">
            <select value={form.branch} onChange={(e) => setForm({ ...form, branch: e.target.value })}
              className="input" data-testid="vf-branch-input">
              {branches.map(b => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
          {canEditNdp && (
            <Field label="NDP — Net Dealer Purchase Price (₹)" testid="vf-ndp">
              <input type="number" min="0" step="1" value={form.ndp ?? ""}
                onChange={(e) => setForm({ ...form, ndp: e.target.value })}
                placeholder="Confidential — Admin/Finance only"
                className="input" data-testid="vf-ndp-input" />
            </Field>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-md text-sm border border-slate-300 hover:bg-slate-50" data-testid="vf-cancel">
            Cancel
          </button>
          <button type="submit" disabled={saving} data-testid="vf-submit"
            className="px-4 py-2 rounded-md text-sm bg-brand-blue bg-brand-blue-hover text-white disabled:opacity-60">
            {saving ? "Saving…" : (isEdit ? "Update" : "Add Vehicle")}
          </button>
        </div>
        <style>{`.input { @apply w-full px-3 py-2 rounded-md border border-slate-300 text-sm outline-none focus:border-blue-800; }`}</style>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
